<template>
  <section class="px-3 py-3 sm:px-5 sm:py-4 lg:px-8 lg:py-6">
    <div class="mx-auto max-w-7xl">
      <div class="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4 lg:rounded-[2rem] lg:p-8">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
          <div class="flex min-w-0 items-center gap-2.5 sm:gap-3 lg:gap-4">
            <router-link
              class="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 hover:text-slate-950 lg:h-10 lg:w-10"
              to="/explore"
              aria-label="Back to flyers"
            >
              <i class="ri-arrow-left-line text-lg lg:text-xl" aria-hidden="true"></i>
            </router-link>
            <img
              class="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-slate-200 sm:h-14 sm:w-14 lg:h-20 lg:w-20 lg:rounded-[1.5rem]"
              :src="profileAvatar"
              :alt="author"
              loading="lazy"
            />
            <div class="min-w-0">
              <p class="m-0 hidden text-xs font-semibold uppercase tracking-[0.14em] text-slate-400 sm:block">User Detail</p>
              <h1 class="truncate text-lg font-semibold text-slate-950 sm:mt-1 sm:text-xl lg:mt-2 lg:text-3xl">{{ author }}</h1>
              <p class="mt-0.5 hidden text-sm font-medium text-slate-500 sm:block lg:mt-2">Flyers published by this user.</p>
              <div class="mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2 lg:mt-3">
                <button
                  v-if="!profileNotFound"
                  class="nf-primary-button nf-primary-button--compact"
                  type="button"
                  :disabled="followSaving || isOwnProfile"
                  @click="toggleFollow"
                >
                  <i :class="following ? 'ri-user-follow-fill' : 'ri-user-add-line'" aria-hidden="true"></i>
                  <span>{{ followButtonLabel }}</span>
                </button>
                <button
                  v-if="!profileNotFound && !isOwnProfile"
                  class="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 lg:min-h-10 lg:gap-2 lg:px-4 lg:text-sm"
                  type="button"
                  @click="openChat"
                >
                  <i class="ri-message-3-line" aria-hidden="true"></i>
                  <span>Chat</span>
                </button>
              </div>
              <p v-if="followError" class="mt-2 text-sm font-medium text-rose-700">{{ followError }}</p>
            </div>
          </div>

          <div class="grid w-full grid-cols-3 gap-1.5 sm:gap-2 lg:w-auto lg:min-w-[22rem] lg:gap-3">
            <div
              v-for="item in statCards"
              :key="item.label"
              class="min-w-0 rounded-xl bg-slate-100 px-1.5 py-1.5 text-center sm:px-3 sm:py-2 lg:rounded-2xl lg:px-4 lg:py-3"
            >
              <div class="truncate text-[0.55rem] font-semibold uppercase tracking-[0.06em] text-slate-400 sm:text-[0.62rem] sm:tracking-[0.08em] lg:text-xs lg:tracking-[0.12em]">{{ item.label }}</div>
              <div class="mt-0.5 truncate text-sm font-semibold tabular-nums text-slate-950 sm:text-base lg:mt-1 lg:text-2xl" :title="String(item.rawValue)">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div class="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <h2 class="m-0 text-2xl font-semibold text-slate-950">Flyers</h2>
          </div>
          <span class="hidden rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 lg:inline-flex">
            {{ flyers.length }} total
          </span>
        </div>

        <div v-if="isLoading" class="py-12 text-center text-sm font-medium text-slate-500">
          Loading flyers...
        </div>

        <div v-else-if="profileNotFound" class="py-12 text-center">
          <p class="m-0 text-lg font-semibold text-slate-900">No user found</p>
          <p class="mt-2 text-sm font-medium text-slate-500">This profile is not available.</p>
        </div>

        <div v-else-if="!flyers.length" class="py-12 text-center">
          <p class="m-0 text-lg font-semibold text-slate-900">No flyers found</p>
          <p class="mt-2 text-sm font-medium text-slate-500">This user does not have visible flyers yet.</p>
        </div>

        <template v-else>
          <div class="mt-5 grid gap-7">
            <section v-for="group in groupedFlyers" :key="group.key" class="grid gap-3">
              <div class="flex items-center gap-3">
                <h3 class="m-0 shrink-0 text-sm font-semibold text-slate-900">{{ group.label }}</h3>
                <span class="h-px flex-1 bg-slate-200"></span>
                <span class="shrink-0 text-xs font-semibold text-slate-400">{{ group.items.length }}</span>
              </div>
              <div class="user-flyer-masonry">
                <FlyerCard v-for="flyer in group.items" :key="flyer.id" :flyer="flyer" :show-author="false" />
              </div>
            </section>
          </div>
          <div ref="loadMoreTarget" class="grid min-h-20 place-items-center">
            <span v-if="isFetchingMore" class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-500 shadow-sm">
              Loading more flyers...
            </span>
            <span v-else-if="hasMoreFlyers" class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-500 shadow-sm">
              Scroll to load more
            </span>
            <span v-else class="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-500">
              You are all caught up.
            </span>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FlyerCard from '@/components/common/FlyerCard.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { formatFlyerListDate, parseApiDate } from '@/utils/date'
import { getFlyerBoardId, normalizeFlyerSaved } from '@/utils/flyerDetail'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const isLoading = ref(false)
const isFetchingMore = ref(false)
const currentPage = ref(0)
const hasMoreFlyers = ref(true)
const loadMoreTarget = ref(null)
const flyers = ref([])
const profileNotFound = ref(false)
const following = ref(false)
const followSaving = ref(false)
const followError = ref('')
const profileMeta = ref({
  followcount: 0,
  followingcount: 0,
  flyersCount: 0,
  userinfo: null
})
let observer

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '')
const routeProfileKey = computed(() => String(route.params.id || '').trim())
const routeProfileIsSlug = computed(() => routeProfileKey.value && !/^\d+$/.test(routeProfileKey.value))
const profileUserId = computed(() => {
  const apiUser = profileMeta.value.userinfo || {}
  return String(firstValue(apiUser.id, apiUser.user_id, apiUser.userid, apiUser.uid, routeProfileIsSlug.value ? '' : routeProfileKey.value, ''))
})
const profileUsername = computed(() => {
  const apiUser = profileMeta.value.userinfo || {}
  return String(firstValue(apiUser.username, apiUser.user_name, routeProfileIsSlug.value ? routeProfileKey.value : '', '')).replace(/^@/, '')
})
const sessionUsername = computed(() => {
  const user = session.user || {}
  const profile = session.profile || {}
  return String(firstValue(user.username, user.user_name, profile.username, profile.user_name, '')).replace(/^@/, '')
})

const author = computed(() => {
  if (profileNotFound.value) return 'No user found'
  const apiUser = profileMeta.value.userinfo || {}
  const first = flyers.value[0]
  return apiUser.name || apiUser.username || first?.author || ''
})

const profileAvatar = computed(() => {
  if (profileNotFound.value) return '/assets/img/av.png'
  const apiUser = profileMeta.value.userinfo || {}
  const first = flyers.value[0]
  return apiUser.user_img || first?.authorAvatar || '/assets/img/av.png'
})

const isOwnProfile = computed(() => {
  const currentUserId = String(session.userId || session.user?.user_id || session.user?.id || '')
  const targetUserId = profileUserId.value
  if (currentUserId && targetUserId && currentUserId === targetUserId) return true
  return Boolean(sessionUsername.value && profileUsername.value && sessionUsername.value === profileUsername.value)
})
const followButtonLabel = computed(() => {
  if (isOwnProfile.value) return 'Your profile'
  if (followSaving.value) return 'Saving...'
  return following.value ? 'Following' : 'Follow'
})

const formatStatCount = (value = 0) => {
  const count = Number(value || 0)
  if (!Number.isFinite(count)) return '0'
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1).replace(/\.0$/, '')}M`
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1).replace(/\.0$/, '')}K`
  return String(count)
}

const makeStatCard = (label, rawValue) => ({
  label,
  rawValue: Number(rawValue || 0),
  value: formatStatCount(rawValue)
})

const statCards = computed(() => {
  const apiUser = profileMeta.value.userinfo || {}
  const followers = apiUser.followcount ||
    apiUser.followers_count ||
    apiUser.followcount_flyer ||
    profileMeta.value.followcount ||
    0
  const followingCount = apiUser.followingcount ||
    apiUser.following_count ||
    apiUser.followingcount_flyer ||
    profileMeta.value.followingcount ||
    0

  return [
    makeStatCard('Posts', apiUser.flyers_count || apiUser.posts_count || profileMeta.value.flyersCount || flyers.value.length || 0),
    makeStatCard('Followers', followers),
    makeStatCard('Following', followingCount)
  ]
})

const normalizeStatus = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === '1' || normalized === 'active' || normalized === 'published' || normalized === 'true') {
    return 'Active'
  }
  if (normalized === '0' || normalized === 'inactive' || normalized === 'draft' || normalized === 'false') {
    return 'Inactive'
  }
  return 'Inactive'
}

const normalizeFollowStatus = (...values) => {
  const value = values.find((item) => item !== undefined && item !== null && item !== '')
  const normalized = String(value ?? '').trim().toLowerCase()
  return ['1', 'true', 'yes', 'following', 'followed'].includes(normalized)
}

const getApiMessage = (payload, fallback) =>
  payload?.message || payload?.msg || payload?.errors?.[0]?.msg || fallback

const getCurrentUserId = () => session.userId || session.user?.user_id || session.user?.id || ''

const requireLogin = () => {
  if (getCurrentUserId()) return true
  router.push({ path: '/login', query: { redirect: route.fullPath } })
  return false
}

const normalizeFlyer = (item = {}, index = 0) => {
  const timeAgo = String(item.time_ago || item.time_Ago || item.timeAgo || '').trim()
  const dateValue = item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || item.date || ''
  const parsedDate = parseApiDate(dateValue)
  const apiGroupKey = String(item.api_group_key || item.group_key || '').trim()
  const apiGroupIndex = Number(item.api_group_index)
  const groupDateValue = apiGroupKey || dateValue
  const parsedGroupDate = parseApiDate(groupDateValue)
  const displayDate = formatFlyerListDate(parsedGroupDate || parsedDate || groupDateValue || dateValue) || 'No date'
  const fallbackImage = Array.isArray(item.images) ? item.images[0] : ''
  const boardId = getFlyerBoardId(item)
  const city = item.city || item.area || item.locality || ''
  const locationText = item.location || item.location_name || item.address || city || item.state || item.street || item.country_id || item.country || ''

  return {
    id: item.id || item.flyer_id || `user-${index}`,
    title: item.title || item.name || item.flyer_title || '',
    category: item.category_name || item.category || item.content_type || 'Flyer',
    description: item.description || item.desc || item.caption || '',
    image: item.image || item.flyer_image || item.image_url || fallbackImage || '',
    param_code: item.param_code || item.paramCode || item.code || item.color_code || item.colorCode || item.color || '',
    code: item.code || item.param_code || item.paramCode || item.color_code || item.colorCode || item.color || '',
    accent: item.accent || item.categoryColor || '#64748b',
    address: item.address || item.flyer_address || item.location_name || item.location || city || '',
    location: locationText,
    city,
    area: item.area || '',
    locality: item.locality || '',
    state: item.state || '',
    street: item.street || '',
    country_id: item.country_id || '',
    country: item.country || '',
    status: normalizeStatus(item.status ?? item.publish_status),
    boardId,
    board_id: boardId,
    saved: normalizeFlyerSaved(item),
    userId: item.user_id || item.userId || item.userid || item.uid || item.created_by || item.created_by_id || item.owner_id || item.author_id || profileUserId.value,
    author: item.author || item.user_name || item.username || `Creator ${routeProfileKey.value}`,
    authorAvatar: item.authorAvatar || item.profile_pic || item.user_image || '/assets/img/av.png',
    time_ago: timeAgo,
    time_Ago: timeAgo,
    date: timeAgo || formatFlyerListDate(parsedDate || dateValue) || 'No date',
    postedAt: item.posted_at || item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || dateValue,
    parsedDate,
    groupKey: parsedGroupDate ? parsedGroupDate.toISOString().slice(0, 10) : (apiGroupKey || (parsedDate ? parsedDate.toISOString().slice(0, 10) : `undated-${index}`)),
    groupSortValue: parsedGroupDate?.getTime?.() || parsedDate?.getTime?.() || 0,
    displayDate,
    apiGroupIndex: Number.isFinite(apiGroupIndex) ? apiGroupIndex : null
  }
}

const flattenDateBuckets = (payload) => {
  if (!payload || typeof payload !== 'object') return []
  const entries = Object.entries(payload)
  return entries.flatMap(([dateKey, items = []], groupIndex) =>
    Array.isArray(items)
      ? items.map((item, itemIndex) => ({
          ...item,
          created_date: item.created_date || dateKey,
          api_group_key: dateKey,
          api_group_index: groupIndex,
          api_item_index: itemIndex
        }))
      : []
  )
}

const extractRows = (data) => {
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.list)) return data.list
  return flattenDateBuckets(data?.list)
}

const extractPagination = (payload) => {
  if (!payload || typeof payload !== 'object') return { currentPage: null, totalPages: null }

  const meta = payload.pagination || payload.meta || payload.page_info || payload.data?.pagination || payload.data?.meta || {}
  const current = Number(payload.current_page || payload.page || meta.current_page || meta.page || meta.currentPage)
  const total = Number(payload.total_pages || payload.last_page || meta.total_pages || meta.last_page || meta.totalPages)

  return {
    currentPage: Number.isFinite(current) && current > 0 ? current : null,
    totalPages: Number.isFinite(total) && total > 0 ? total : null
  }
}

const mergeFlyers = (existing, incoming) => {
  const flyerMap = new Map(existing.map((flyer) => [String(flyer.id), flyer]))
  incoming.forEach((flyer) => flyerMap.set(String(flyer.id), flyer))
  return Array.from(flyerMap.values())
}

const sortFlyers = () => {
  if (flyers.value.some((flyer) => Number.isFinite(flyer.apiGroupIndex))) return
  flyers.value.sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
}

const groupedFlyers = computed(() => {
  const groups = new Map()

  flyers.value.forEach((flyer) => {
    if (!groups.has(flyer.groupKey)) {
      groups.set(flyer.groupKey, {
        key: flyer.groupKey,
        label: flyer.displayDate || 'No date',
        sortValue: flyer.groupSortValue || flyer.parsedDate?.getTime?.() || 0,
        apiGroupIndex: flyer.apiGroupIndex,
        items: []
      })
    }

    groups.get(flyer.groupKey).items.push(flyer)
  })

  const values = Array.from(groups.values())
  const hasApiOrder = values.some((group) => Number.isFinite(group.apiGroupIndex))

  if (hasApiOrder) return values

  return values.sort((a, b) => {
    return b.sortValue - a.sortValue
  })
})

const getProfileLookupPayload = () => routeProfileIsSlug.value
  ? { username: routeProfileKey.value }
  : { user_id: routeProfileKey.value }

const observeLoadMoreTarget = async () => {
  await nextTick()
  if (!observer) return
  observer.disconnect()
  if (!loadMoreTarget.value || !hasMoreFlyers.value) return
  observer.observe(loadMoreTarget.value)
}

const loadFlyers = async ({ page = 1, append = false } = {}) => {
  if (append) {
    if (isLoading.value || isFetchingMore.value || !hasMoreFlyers.value) return
    isFetchingMore.value = true
  } else {
    isLoading.value = true
    currentPage.value = 0
    hasMoreFlyers.value = true
    flyers.value = []
    following.value = false
    profileNotFound.value = false
  }

  try {
    const userId = profileUserId.value || (routeProfileIsSlug.value ? '' : routeProfileKey.value)
    const loginUserId = getCurrentUserId()
    const { data } = await api.listAddedFlyerLive({
      page,
      ...getProfileLookupPayload(),
      login_user_id: loginUserId
    })

    if (!append) {
      profileMeta.value = {
        followcount: Number(data?.followcount || data?.followers_count || data?.userinfo?.followcount || data?.userinfo?.followers_count || data?.userinfo?.followcount_flyer || 0),
        followingcount: Number(data?.followingcount || data?.following_count || data?.userinfo?.followingcount || data?.userinfo?.following_count || data?.userinfo?.followingcount_flyer || 0),
        flyersCount: Number(data?.flyers_count || data?.posts_count || data?.userinfo?.flyers_count || data?.userinfo?.posts_count || 0),
        userinfo: data?.userinfo || null
      }
      const resolvedTargetUserId = firstValue(data?.userinfo?.id, data?.userinfo?.user_id, data?.userinfo?.userid, data?.userinfo?.uid, userId)
      following.value = loginUserId && resolvedTargetUserId && String(loginUserId) !== String(resolvedTargetUserId)
        ? normalizeFollowStatus(
            data?.followstatus,
            data?.follow_status,
            data?.is_following,
            data?.userinfo?.followstatus,
            data?.userinfo?.follow_status,
            data?.userinfo?.is_following
          )
        : false
    }

    const rows = extractRows(data)
    const hasUserInfo = Boolean(data?.userinfo && Object.keys(data.userinfo).length)
    if (!append && data?.status && data.status !== 'ok') {
      profileNotFound.value = true
      hasMoreFlyers.value = false
      return
    }
    if (!append && !hasUserInfo && !rows.length) {
      profileNotFound.value = true
      hasMoreFlyers.value = false
      return
    }
    const normalizedRows = rows.map((item, index) => normalizeFlyer(item, `${page}-${index}`))
    flyers.value = append ? mergeFlyers(flyers.value, normalizedRows) : normalizedRows
    currentPage.value = normalizedRows.length ? page : Math.max(currentPage.value, page - 1)

    const pagination = extractPagination(data)
    hasMoreFlyers.value = pagination.totalPages
      ? (pagination.currentPage || page) < pagination.totalPages
      : normalizedRows.length > 0
  } catch {
    if (!append) {
      flyers.value = []
      profileMeta.value = {
        followcount: 0,
        followingcount: 0,
        flyersCount: 0,
        userinfo: null
      }
      profileNotFound.value = true
      hasMoreFlyers.value = false
      following.value = false
    } else {
      hasMoreFlyers.value = false
    }
  } finally {
    sortFlyers()
    isLoading.value = false
    isFetchingMore.value = false
    observeLoadMoreTarget()
  }
}

const loadMoreFlyers = () => {
  loadFlyers({ page: currentPage.value + 1, append: true })
}

const toggleFollow = async () => {
  if (isOwnProfile.value || followSaving.value) return

  const userId = getCurrentUserId()
  const followUserId = profileUserId.value || routeProfileKey.value
  if (!requireLogin()) return
  if (!followUserId) return

  const previous = following.value
  const nextFollowing = !following.value
  following.value = nextFollowing
  const previousFollowCount = Number(profileMeta.value.followcount || 0)
  profileMeta.value.followcount = Math.max(0, previousFollowCount + (nextFollowing ? 1 : -1))
  followSaving.value = true
  followError.value = ''

  try {
    const { data } = await api.flyer_follow_user({
      follow_user_id: followUserId,
      user_id: userId
    })

    if (data?.status && data.status !== 'ok') {
      throw new Error(getApiMessage(data, 'Follow could not be updated.'))
    }

    if (
      data?.followstatus !== undefined ||
      data?.follow_status !== undefined ||
      data?.is_following !== undefined
    ) {
      following.value = normalizeFollowStatus(data.followstatus, data.follow_status, data.is_following)
    }
  } catch (error) {
    following.value = previous
    profileMeta.value.followcount = previousFollowCount
    followError.value = getApiMessage(error?.response?.data, error?.message || 'Follow could not be updated.')
  } finally {
    followSaving.value = false
  }
}

const openChat = () => {
  if (!requireLogin()) return
  const toId = String(profileUserId.value || routeProfileKey.value || '')
  sessionStorage.setItem('netflyr.chatTarget', JSON.stringify({
    to_id: toId,
    flyer_id: '',
    to_name: author.value || '',
    to_image: profileAvatar.value || '',
    source: 'user',
    savedAt: Date.now()
  }))

  router.push('/chats')
}

watch(() => route.params.id, () => {
  loadFlyers({ page: 1, append: false })
})

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        loadMoreFlyers()
      }
    },
    { rootMargin: '260px 0px' }
  )
  loadFlyers({ page: 1, append: false })
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<style scoped>
.user-flyer-masonry {
  column-gap: 1rem;
  columns: 2;
}

.user-flyer-masonry :deep(.flyer-card) {
  break-inside: avoid;
}

@media (min-width: 640px) {
  .user-flyer-masonry {
    columns: 2;
  }
}

@media (min-width: 900px) {
  .user-flyer-masonry {
    columns: 3;
  }
}

@media (min-width: 1180px) {
  .user-flyer-masonry {
    columns: 4;
  }
}

@media (min-width: 1536px) {
  .user-flyer-masonry {
    columns: 5;
  }
}
</style>
